<div class="bai-journey-root" aria-label="Client journey timeline">

  @if (loading()) {
    <!-- Skeleton loading state -->
    <div class="bai-journey-skeleton" aria-busy="true" aria-label="Loading journey timeline">
      <div class="bai-journey-toolbar-skeleton">
        @for (_ of [1,2,3,4,5,6]; track $index) {
          <p-skeleton width="90px" height="32px" borderRadius="1rem" />
        }
      </div>
      @for (_ of [1,2,3,4,5]; track $index) {
        <div class="bai-journey-card-skeleton">
          <div class="bai-skeleton-time">
            <p-skeleton width="60px" height="14px" />
            <p-skeleton width="50px" height="12px" />
          </div>
          <div class="bai-skeleton-connector">
            <p-skeleton width="12px" height="12px" borderRadius="50%" />
          </div>
          <div class="bai-skeleton-card">
            <p-skeleton width="120px" height="12px" />
            <p-skeleton width="100%" height="16px" />
            <p-skeleton width="80%" height="14px" />
            <p-skeleton width="60px" height="20px" borderRadius="1rem" />
          </div>
        </div>
      }
    </div>
  } @else if (!state()) {
    <!-- Empty state: no session selected -->
    <div class="bai-journey-empty" role="status" aria-label="No client selected">
      <i class="pi pi-history bai-journey-empty-icon" aria-hidden="true"></i>
      <p class="bai-journey-empty-title">No client selected</p>
      <p class="bai-journey-empty-sub">Select a client to view their journey timeline.</p>
    </div>
  } @else {
    @let s = state()!;

    <!-- Filter toolbar -->
    <div class="bai-journey-toolbar" role="toolbar" aria-label="Journey filters">
      <!-- Quick filter chips -->
      <div class="bai-journey-chips" role="group" aria-label="Quick filters">
        <bai-filter-chip-group
          [options]="quickFilterOptions"
          [selected]="activeFilter()"
          ariaLabel="Quick filters"
          (selectionChange)="onQuickFilterSelection($event)"
        />
      </div>

      <!-- Search -->
      <div class="bai-journey-search-wrap">
        <i class="pi pi-search bai-journey-search-icon" aria-hidden="true"></i>
        <input
          class="bai-journey-search"
          type="search"
          placeholder="Search events…"
          [ngModel]="searchQuery()"
          (ngModelChange)="searchQuery.set($event)"
          aria-label="Search journey events"
        />
      </div>

      <!-- Source filter -->
      <select
        class="bai-journey-select"
        [ngModel]="sourceFilter()"
        (ngModelChange)="sourceFilter.set($event)"
        aria-label="Filter by source"
      >
        @for (src of sourceTypes; track trackSource($index, src)) {
          <option [value]="src">{{ sourceTypeLabel(src) }}</option>
        }
      </select>

      <!-- Sort direction -->
      <button
        class="bai-journey-sort-btn"
        type="button"
        (click)="sortDir.set(sortDir() === 'desc' ? 'asc' : 'desc')"
        [attr.aria-label]="sortDir() === 'desc' ? 'Sorted newest first, click for oldest first' : 'Sorted oldest first, click for newest first'"
        [attr.aria-pressed]="sortDir() === 'asc'"
      >
        <i [class]="sortDir() === 'desc' ? 'pi pi-sort-amount-down' : 'pi pi-sort-amount-up'"></i>
        {{ sortDir() === 'desc' ? 'Newest first' : 'Oldest first' }}
      </button>
    </div>

    <!-- Event count summary -->
    <div class="bai-journey-count" aria-live="polite" aria-atomic="true">
      @if (filteredEvents().length === s.events.length) {
        <span>{{ s.events.length }} events</span>
      } @else {
        <span>{{ filteredEvents().length }} of {{ s.events.length }} events</span>
      }
    </div>

    <!-- Timeline feed -->
    @if (filteredEvents().length === 0) {
      <div class="bai-journey-empty" role="status" aria-label="No matching events">
        <i class="pi pi-filter-slash bai-journey-empty-icon" aria-hidden="true"></i>
        <p class="bai-journey-empty-title">No events match your filters</p>
        <p class="bai-journey-empty-sub">Try adjusting your search or filter criteria.</p>
      </div>
    } @else {
      <div class="bai-timeline" role="list" aria-label="Journey timeline">
        @for (event of filteredEvents(); track trackEvent($index, event)) {
          <div class="bai-timeline-row" role="listitem">

            <!-- Time column -->
            <div class="bai-timeline-time" aria-hidden="true">
              <span class="bai-timeline-date">{{ formatDate(event.occurredAt) }}</span>
              <span class="bai-timeline-time-str">{{ formatTime(event.occurredAt) }}</span>
              <span class="bai-timeline-relative">{{ relativeTime(event.occurredAt) }}</span>
            </div>

            <!-- Connector: line + dot -->
            <div class="bai-timeline-connector" aria-hidden="true">
              <div class="bai-timeline-line"></div>
              <div class="bai-timeline-dot" [class]="dotColorClass(event.eventType)"></div>
            </div>

            <!-- Event card -->
            <article
              class="bai-event-card"
              [class]="cardAccentClass(event.eventType)"
              (click)="openEventDetail(event)"
              (keydown.enter)="openEventDetail(event)"
              (keydown.space)="openEventDetail(event); $event.preventDefault()"
              tabindex="0"
              role="button"
              [attr.aria-label]="'View details: ' + event.title + ', ' + eventTypeLabel(event.eventType) + ', ' + formatDate(event.occurredAt)"
            >
              <!-- Card header row -->
              <div class="bai-event-card-header">
                <span class="bai-event-channel-icon" [attr.aria-label]="event.channel">
                  <i [class]="channelIcon(event.channel)" aria-hidden="true"></i>
                </span>
                <span class="bai-event-type-label">{{ eventTypeLabel(event.eventType) }}</span>

                @if (event.direction) {
                  <span class="bai-event-direction" [attr.aria-label]="event.direction">
                    <i [class]="event.direction === 'inbound' ? 'pi pi-arrow-down' : 'pi pi-arrow-up'" aria-hidden="true"></i>
                  </span>
                }

                @if (event.severity) {
                  <p-tag
                    [value]="severityLabel(event.severity)"
                    [severity]="severityTagSeverity(event.severity)"
                    styleClass="bai-severity-tag"
                  />
                }

                <details class="bai-journey-kebab" (click)="$event.stopPropagation(); $any($event.target).closest('[class*=__action]') && $any($event.currentTarget).removeAttribute('open')">
                  <summary class="bai-journey-kebab__btn" aria-label="Event options">
                    <i class="pi pi-bars" aria-hidden="true"></i>
                  </summary>
                  <button
                    type="button"
                    class="bai-journey-kebab__action"
                    (click)="addEventToCopilot(event)"
                  >
                    Add to Copilot
                  </button>
                </details>
              </div>

              <!-- Title + summary -->
              <p class="bai-event-title">{{ event.title }}</p>
              <p class="bai-event-summary">{{ event.summary }}</p>

              <!-- Actor + tags row -->
              <div class="bai-event-meta-row">
                <span class="bai-event-source">
                  <i [class]="sourceTypeIcon(event.sourceType)" aria-hidden="true"></i>
                  {{ event.sourceName }}
                </span>

                @if (event.tags.length > 0) {
                  <div class="bai-event-tags" aria-label="Tags">
                    @for (tag of event.tags; track $index) {
                      <span class="bai-tag-pill">{{ tag }}</span>
                    }
                  </div>
                }

                @if (event.detailPayload.metricSnippet) {
                  <span class="bai-event-metric">
                    <i class="pi pi-chart-bar" aria-hidden="true"></i>
                    {{ event.detailPayload.metricSnippet }}
                  </span>
                }
              </div>
            </article>
          </div>
        }
      </div>
    }
  }
</div>
